import React, { useEffect, useState } from "react";
import {
  DialogTitle,
  Box,
  Typography,
  Button,
  IconButton,
  Chip
} from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from "@mui/icons-material";
import { colors, fonts, spacing, borderRadius } from "../../../constants";

/**
 * QueryModalHeader Component
 * Dialog header with title, escalation chip, and action buttons
 *
 * @param {Object} query - Query object
 * @param {boolean} isEscalated - Whether query is escalated
 * @param {string} escalatedToName - Name of user query is escalated to
 * @param {boolean} isEscalatedViewOnly - Whether user has view-only access
 * @param {boolean} isClosed - Whether query is closed
 * @param {function} onEdit - Edit handler
 * @param {function} onDelete - Delete handler
 * @param {function} onClose - Close handler
 */
const QueryModalHeader = ({
  query,
  isEscalated,
  escalatedToName,
  isEscalatedViewOnly,
  isClosed,
  onEdit,
  onDelete,
  onClose
}) => {
  const [accessRights, setAccessRights] = useState({});

  useEffect(() => {
    try {
      const rights = JSON.parse(localStorage.getItem("AccessRights")) || {};
      setAccessRights(rights);
    } catch {
      setAccessRights({});
    }
  }, []);
  const shouldShowEdit = accessRights["1041"] === "Y";
  const shouldShowDelete = accessRights["1044"] === "Y";
  return (
    <DialogTitle
      sx={{
        padding: `${spacing.xs} ${spacing.sm}`,
        borderBottom: "none",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        background: colors.primaryGradient,
        position: "sticky",
        top: 0,
        zIndex: 1,
        boxShadow: `0 18px 32px ${colors.primaryShadow}`
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: spacing.xs,
          flexWrap: "wrap"
        }}
      >
        <Typography
          component="div"
          variant="h5"
          sx={{
            fontWeight: fonts.weightBold,
            fontSize: fonts.fontSizeLg,
            color: colors.textWhite,
            letterSpacing: "-0.5px"
          }}
        >
          Query Details
        </Typography>
      </Box>
      <Box sx={{ display: "flex", gap: spacing.xs, alignItems: "center" }}>
        {shouldShowEdit && onEdit && !isEscalatedViewOnly && !isClosed && (
          <Button
            onClick={() => onEdit(query)}
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            sx={{
              textTransform: "none",
              borderRadius: borderRadius.button,
              padding: `${spacing.xs} ${spacing.sm}`,
              borderColor: colors.primaryDark,
              color: colors.primaryDark,
              fontSize: fonts.fontSizeSm,
              backgroundColor: colors.primarySoft,
              "&:hover": {
                borderColor: colors.primary,
                backgroundColor: colors.primaryTint
              }
            }}
          >
            Edit
          </Button>
        )}
        {shouldShowDelete && onDelete && !isEscalatedViewOnly && (
          <Button
            onClick={() => onDelete(query)}
            variant="outlined"
            size="small"
            startIcon={<DeleteIcon />}
            sx={{
              textTransform: "none",
              borderRadius: borderRadius.button,
              padding: `${spacing.xs} ${spacing.sm}`,
              borderColor: colors.primaryDark,
              color: colors.primaryDark,
              fontSize: fonts.fontSizeSm,
              backgroundColor: colors.primarySoft,
              "&:hover": {
                borderColor: colors.primary,
                backgroundColor: colors.primaryTint
              }
            }}
          >
            Delete
          </Button>
        )}
        <IconButton
          onClick={onClose}
          sx={{
            color: colors.textWhite,
            padding: spacing.xs,
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: colors.primaryTint,
              color: colors.textWhite,
              transform: "rotate(90deg)"
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
    </DialogTitle>
  );
};

export default QueryModalHeader;
